import React from 'react';
import Image from 'next/image';
import { discardHTMLTags, getCurrencySymbol, getFinalDiscountTag, getProductDetailHref } from '@/constants/hooks';
import OfferOutUrl from '@/components/shared/OfferOutUrl';
import { BrandedProduct, BrandedProductMerchant } from '@/services/dataTypes';

interface BrandedProductCardProps {
  product: BrandedProduct;
  domain: string;
  imageSrc: string;
  merchant: BrandedProductMerchant;
  mer_slug_type: string;
}

const BrandedProductCard = ({ product, domain, imageSrc, merchant, mer_slug_type }: BrandedProductCardProps) => {
  const type = product?.offer_type?.name;
  const originalPrice = product?.original_price ? parseFloat(product?.original_price) : 0;
  const salePrice = product?.sale_price ? parseFloat(product?.sale_price) : 0;
  const discountPercent = originalPrice > 0 && salePrice > 0 && salePrice < originalPrice
    ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
    : null;

  const finalDiscountTag = getFinalDiscountTag(
    product?.offer_title,
    discountPercent,
  );

  const productUrl = product?.slug ? getProductDetailHref(merchant, mer_slug_type, product?.slug, product?.category?.slug) : null;

  return (
    <div className="group relative bg-white rounded-xl border border-[#E5E7EB] transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-[#F97316] overflow-hidden">

      {/* Premium Discount Badge */}
      {finalDiscountTag !== null && (
        <div className="absolute top-2.5 right-2.5 z-10">
          <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-black bg-[#DC2626] text-white uppercase tracking-wide">
            {finalDiscountTag}
          </span>
        </div>
      )}

      {/* Premium Image Container */}
      <div className="relative h-36 bg-gradient-to-br from-[#F8FAFC] to-[#F8FAFC] overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center p-3">
          <div className="relative w-full h-full">
            <Image
              src={imageSrc}
              alt={discardHTMLTags(product?.offer_title) || 'Product'}
              fill
              className="object-contain transition-all duration-300 group-hover:scale-105"
              unoptimized
            />
          </div>
        </div>

        {/* Merchant Badge Overlay */}
        {merchant?.merchant_name && (
          <div className="absolute bottom-2.5 left-2.5">
            <div className="bg-white/95 backdrop-blur-sm px-2 py-1 rounded-md shadow-sm border border-[#E5E7EB]">
              <p className="text-[9px] font-semibold text-[#1E3A5F] uppercase tracking-wide">
                {merchant.merchant_name}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Premium Content Section */}
      <div className="p-3">
        {/* Product Title */}
        <h3 className="text-sm font-bold text-[#111827] line-clamp-2 leading-snug mb-2 min-h-[2.5rem] group-hover:text-[#F97316] transition-colors">
          {discardHTMLTags(product?.offer_title)}
        </h3>

        {/* Price Section with Premium Styling */}
        <div className="flex items-baseline justify-between mb-2">
          {type === 'product' && (salePrice > 0 || originalPrice > 0) ? (
            <div className="flex items-baseline gap-2">
              {product?.sale_price && salePrice > 0 && (
                <span className="text-lg font-black text-[#15803D]">
                  {getCurrencySymbol(product?.currency)}{product?.sale_price}
                </span>
              )}
              {product?.original_price && originalPrice > 0 && salePrice > 0 && (
                <span className="text-xs font-semibold text-[#4B5563] line-through">
                  {getCurrencySymbol(product?.currency)}{product?.original_price}
                </span>
              )}
              {!salePrice && originalPrice > 0 && (
                <span className="text-lg font-black text-[#15803D]">
                  {getCurrencySymbol(product?.currency)}{product?.original_price}
                </span>
              )}
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-[#FFF7ED] to-[#FFEDD5] rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-[#F97316]">
                <path fillRule="evenodd" d="M5.5 3A2.5 2.5 0 003 5.5v2.879a2.5 2.5 0 00.732 1.767l6.5 6.5a2.5 2.5 0 003.536 0l2.878-2.878a2.5 2.5 0 000-3.536l-6.5-6.5A2.5 2.5 0 008.38 3H5.5zM6 7a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <span className="text-[10px] font-black text-[#EA580C] uppercase tracking-wide">Exclusive</span>
            </div>
          )}
        </div>

        {/* Premium CTA Section */}
        <div className="flex items-center gap-2">
          {productUrl && (
            <a
              href={productUrl}
              aria-label={`View details: ${discardHTMLTags(product?.offer_title) || 'product'}`}
              title="View details"
              className="flex items-center justify-center px-3 py-2 border border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white text-xs font-bold rounded-lg transition-all duration-200 no-underline group/link"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true" focusable="false">
                <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
                <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
            </a>
          )}

          <div className="flex-1">
            <OfferOutUrl
              unique_id={product?.unique_id}
              outUrl={product?.url}
              merchantHref={productUrl || `/products`}
              domain={domain}
              customClass="w-full"
            >
              <button className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-black text-xs py-2.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-sm shadow-[#F97316]/30 hover:shadow-md hover:shadow-[#EA580C]/40 active:scale-[0.98] uppercase tracking-wide">
                <span>
                  {product?.coupon_code ? 'Get Code' : (type === 'product' ? 'View Product' : 'Get Deal')}
                </span>
                {product?.coupon_code && (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </OfferOutUrl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandedProductCard;
